/**
 * Decision History Page
 * 
 * Makes C3.F3 (Decision memory reconstructs without oral history) true.
 * Provides a searchable, browsable view of all prioritization decisions.
 */

import { useState, useEffect } from 'react';
import { useUserStore } from '../stores/prioritization/userStore';
import {
  History,
  Search,
  Filter,
  Download,
  Calendar,
  User,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  AlertCircle,
  Layers,
} from 'lucide-react';
import { PageHeader } from '../components/prioritization/PageHeader';
import { StrategyConnectivityHelpButton } from './prioritization/strategy/StrategyConnectivityDocModal';
import {
  listDecisionsByWorkspace,
  generateDecisionSummary,
  decisionsToCsvRows,
  type DecisionRecord,
  type DecisionType,
  type DecisionSummary,
} from '../lib/decisionMemory';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import { exportToCsv } from '../lib/reportExport';

// ============================================================================
// Helpers
// ============================================================================

const DECISION_TYPE_CONFIG: Record<DecisionType, { 
  label: string; 
  icon: typeof History; 
  color: string;
}> = {
  priority_set: { label: 'Priority List', icon: Layers, color: 'text-[var(--text-ter)]' },
  priority_changed: { label: 'Priority Changed', icon: ArrowUpRight, color: 'text-[var(--text-ter)]' },
  item_added: { label: 'Item Added', icon: ArrowUpRight, color: 'text-[var(--green)]' },
  item_removed: { label: 'Item Removed', icon: ArrowDownRight, color: 'text-[var(--coral)]' },
  cut_line_moved: { label: 'Cut Line Moved', icon: Layers, color: 'text-[var(--mustard)]' },
  list_published: { label: 'List Published', icon: CheckCircle, color: 'text-[var(--green)]' },
  override_applied: { label: 'Override Applied', icon: AlertCircle, color: 'text-[var(--mustard)]' },
  criteria_changed: { label: 'Criteria Changed', icon: Filter, color: 'text-[var(--text-ter)]' },
  tool_selected: { label: 'Tool Selected', icon: Layers, color: 'text-[var(--text-ter)]' },
  session_completed: { label: 'Session Completed', icon: CheckCircle, color: 'text-consulting-slate-blue-500' },
};

function DecisionCard({ decision }: { decision: DecisionRecord }) {
  const config = DECISION_TYPE_CONFIG[decision.type] || {
    label: decision.type,
    icon: History,
    color: 'text-[var(--text-ter)]',
  };
  const Icon = config.icon;
  
  const timeAgo = getTimeAgo(new Date(decision.timestamp));
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 hover:border-[var(--rule)] transition-colors">
      <div className="flex items-start gap-3">
        <div className={`p-2  bg-[var(--surface)] ${config.color}`}>
          <Icon className="h-4 w-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
              {config.label}
            </span>
            <span className="text-xs text-[var(--text-ter)]">•</span>
            <span className="text-xs text-[var(--text-ter)]">{timeAgo}</span>
          </div>
          
          <p className="text-sm font-medium text-[var(--text-pri)]">
            {decision.decision.summary}
          </p>
          
          {decision.rationale && (
            <p className="mt-1 text-sm text-[var(--text-sec)] italic">
              "{decision.rationale}"
            </p>
          )}
          
          <div className="mt-2 flex items-center gap-4 text-xs text-[var(--text-ter)]">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{decision.actorRole}</span>
            </div>
            
            {decision.context.toolUsed && (
              <div className="flex items-center gap-1">
                <Layers className="h-3 w-3" />
                <span>{decision.context.toolUsed}</span>
              </div>
            )}
            
            {decision.sessionId && (
              <div className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                <span className="font-mono">{decision.sessionId.slice(0, 8)}...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString();
}

// ============================================================================
// Main Component
// ============================================================================

export default function DecisionHistoryPage() {
  const userId = useUserStore((state) => state.userId);
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);

  const [decisions, setDecisions] = useState<DecisionRecord[]>([]);
  const [summary, setSummary] = useState<DecisionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<DecisionType[]>([]);

  useEffect(() => {
    async function loadData() {
      if (!userId) {
        setLoading(false);
        setDecisions([]);
        setSummary(null);
        return;
      }
      if (!currentWorkspaceId) {
        setLoading(false);
        setDecisions([]);
        setSummary(null);
        return;
      }

      setLoading(true);
      try {
        const [decisionList, summaryData] = await Promise.all([
          listDecisionsByWorkspace(currentWorkspaceId, {
            limit: 100,
            types: selectedTypes.length > 0 ? selectedTypes : undefined,
          }),
          generateDecisionSummary(
            currentWorkspaceId,
            new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
            new Date()
          ),
        ]);

        setDecisions(decisionList);
        setSummary(summaryData);
      } catch (error) {
        console.error('Failed to load decisions:', error);
        setDecisions([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [userId, currentWorkspaceId, selectedTypes]);
  
  const filteredDecisions = decisions.filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return d.decision.summary.toLowerCase().includes(query) ||
           d.rationale?.toLowerCase().includes(query);
  });
  
  const handleExport = () => {
    exportToCsv('decision-history.csv', decisionsToCsvRows(filteredDecisions));
  };

  const toggleTypeFilter = (type: DecisionType) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };
  
  if (!currentWorkspaceId) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4">
        <div className="p-8 text-center text-[var(--text-ter)] border border-[var(--rule)] bg-[var(--surface)]">
          <History className="h-12 w-12 mx-auto text-[var(--text-ter)] mb-3" />
          <p className="font-medium text-[var(--text-sec)]">Select a workspace</p>
          <p className="text-sm mt-1">Choose a workspace to view decision history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<History className="h-4 w-4 text-consulting-slate-blue-600" />}
        iconBg="bg-consulting-slate-blue-50"
        title="Decision History"
        subtitle="Complete record of prioritization decisions"
        actions={
          <div className="flex items-center gap-2">
            <StrategyConnectivityHelpButton context="decisions" />
            {filteredDecisions.length > 0 && (
              <button
                onClick={handleExport}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors text-sm font-medium"
                title="Download the listed decisions as a CSV file"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            )}
          </div>
        }
      />
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="text-3xl font-bold text-[var(--text-pri)]">{summary.totalDecisions}</div>
            <div className="text-sm text-[var(--text-sec)] mt-1">Decisions (30 days)</div>
          </div>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="text-3xl font-bold text-consulting-slate-blue-600">{summary.participants.length}</div>
            <div className="text-sm text-[var(--text-sec)] mt-1">Participants</div>
          </div>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">{summary.byType.list_published || 0}</div>
            <div className="text-sm text-[var(--text-sec)] mt-1">Lists Published</div>
          </div>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="text-3xl font-bold text-[var(--mustard)]">{summary.byType.override_applied || 0}</div>
            <div className="text-sm text-[var(--text-sec)] mt-1">Overrides</div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              placeholder="Search decisions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
            />
          </div>
          
          {/* Type Filters */}
          <div className="flex flex-wrap gap-2">
            {(['list_published', 'override_applied', 'priority_changed', 'cut_line_moved'] as DecisionType[]).map(type => {
              const config = DECISION_TYPE_CONFIG[type];
              const isSelected = selectedTypes.includes(type);
              
              return (
                <button
                  key={type}
                  onClick={() => toggleTypeFilter(type)}
                  className={`inline-flex items-center gap-1 px-3 py-1.5  text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700'
                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  {config.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Decision List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-consulting-slate-blue-600" />
        </div>
      ) : filteredDecisions.length === 0 ? (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-8 text-center">
          <History className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-pri)]">No decisions yet</h3>
          <p className="text-[var(--text-sec)] mt-1">
            Decisions will appear here as you prioritize and publish
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDecisions.map(decision => (
            <DecisionCard key={decision.id} decision={decision} />
          ))}
        </div>
      )}
      
      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-[var(--rule)] text-xs text-[var(--text-ter)] text-center">
        <Calendar className="h-4 w-4 inline mr-1" />
        Showing decisions from the last 30 days
      </div>
    </div>
  );
}


